import type { PluginListenerHandle } from '@capacitor/core';

import type {
  LynxBlePeerGpsUpdateEvent,
  LynxNativeEvent,
  LynxScanResultEvent,
  LynxUwbTokenReceivedEvent,
  LynxUwbUpdateEvent,
} from '../native/advertiserDefinitions';
import { LynxAdvertiser } from '../native/LynxAdvertiser';

export type ScanResultHandler = (event: LynxScanResultEvent) => void;
export type UwbUpdateHandler = (event: LynxUwbUpdateEvent) => void;
export type UwbPeerRemovedHandler = (event: {
  deviceId: string;
  userId: string;
  reason: string;
}) => void;
export type UwbTokenReceivedHandler = (event: LynxUwbTokenReceivedEvent) => void;
export type BlePeerGpsUpdateHandler = (event: LynxBlePeerGpsUpdateEvent) => void;

type Unsubscribe = () => void | Promise<void>;

/** Injectable BLE bridge — production wraps LynxAdvertiser; tests use a fake. */
export interface ProximityBleBridge {
  startAdvertising(options: {
    serviceUuid: string;
    userId: string;
    liveUntilMs?: number | null;
    beaconPrivacy?: boolean;
    beaconShadow?: boolean;
  }): Promise<void>;
  stopAdvertising(): Promise<void>;
  /** Friend allowlist for native GATT connects (absent on fakes; older native builds reject, which is ignored). */
  setRosterIds?(ids: readonly string[] | null): Promise<void>;
  /** Day keys for the private beacon (absent on fakes; older native builds reject, which is ignored). */
  setBeaconKeys?(keys: readonly { userId: string; day: string; key: string }[]): Promise<void>;
  startScanning(onResult: ScanResultHandler): Promise<void>;
  stopScanning(): Promise<void>;
  isUwbAvailable(): Promise<{ supported: boolean }>;
  startUwbSession(options: { deviceId: string }): Promise<{ ok: boolean; error?: string }>;
  runUwbSessionWithToken(options: {
    deviceId: string;
    peerToken: string;
  }): Promise<{ ok: boolean; error?: string }>;
  stopUwbSession(): Promise<{ ok: boolean }>;
  updateGpsForBroadcast(options: {
    lat: number;
    lng: number;
    accuracy: number;
    timestampMs: number;
  }): Promise<{ ok: boolean }>;
  addUwbUpdateListener(handler: UwbUpdateHandler): Promise<Unsubscribe>;
  addUwbPeerRemovedListener(handler: UwbPeerRemovedHandler): Promise<Unsubscribe>;
  addUwbSessionSuspendedListener(handler: () => void): Promise<Unsubscribe>;
  addUwbTokenReceivedListener(handler: UwbTokenReceivedHandler): Promise<Unsubscribe>;
  addBlePeerGpsUpdateListener(handler: BlePeerGpsUpdateHandler): Promise<Unsubscribe>;
  getBluetoothState(): Promise<{ state: string }>;
  addBluetoothStateListener(handler: (state: string) => void): Promise<Unsubscribe>;
  /** Native BLE/UWB lifecycle events buffered since the last drain (absent on fakes and older native builds). */
  drainNativeEvents?(): Promise<{ events: LynxNativeEvent[]; dropped: number }>;
}

async function addListenerUnsub<E extends string>(
  advertiser: Pick<typeof LynxAdvertiser, 'addListener'>,
  eventName: E,
  handler: (data: never) => void,
): Promise<Unsubscribe> {
  const handle = await advertiser.addListener(eventName as 'scanResult', handler as never);
  return async () => {
    await handle.remove();
  };
}

export function createCapacitorBleBridge(
  advertiser: Pick<
    typeof LynxAdvertiser,
    | 'startAdvertising'
    | 'stopAdvertising'
    | 'setRosterIds'
    | 'setBeaconKeys'
    | 'startScanning'
    | 'stopScanning'
    | 'isUwbAvailable'
    | 'startUwbSession'
    | 'runUwbSessionWithToken'
    | 'stopUwbSession'
    | 'updateGpsForBroadcast'
    | 'getBluetoothState'
    | 'drainNativeEvents'
    | 'addListener'
  > = LynxAdvertiser,
): ProximityBleBridge {
  let scanListener: PluginListenerHandle | null = null;

  return {
    async startAdvertising(options) {
      await advertiser.startAdvertising(options);
    },

    async stopAdvertising() {
      await advertiser.stopAdvertising();
    },

    async setRosterIds(ids) {
      await advertiser.setRosterIds({ ids: ids === null ? null : [...ids] });
    },

    async setBeaconKeys(keys) {
      await advertiser.setBeaconKeys({ keys: keys.map((k) => ({ ...k })) });
    },

    async startScanning(onResult) {
      if (scanListener) {
        await scanListener.remove();
        scanListener = null;
      }
      scanListener = await advertiser.addListener('scanResult', (data) => {
        onResult(data);
      });
      await advertiser.startScanning();
    },

    async stopScanning() {
      try {
        await advertiser.stopScanning();
      } finally {
        await scanListener?.remove();
        scanListener = null;
      }
    },

    isUwbAvailable() {
      return advertiser.isUwbAvailable();
    },

    startUwbSession(options) {
      return advertiser.startUwbSession(options);
    },

    runUwbSessionWithToken(options) {
      return advertiser.runUwbSessionWithToken(options);
    },

    stopUwbSession() {
      return advertiser.stopUwbSession();
    },

    updateGpsForBroadcast(options) {
      return advertiser.updateGpsForBroadcast(options);
    },

    addUwbUpdateListener(handler) {
      return addListenerUnsub(advertiser, 'uwbUpdate', handler);
    },

    addUwbPeerRemovedListener(handler) {
      return addListenerUnsub(advertiser, 'uwbPeerRemoved', handler);
    },

    addUwbSessionSuspendedListener(handler) {
      return addListenerUnsub(advertiser, 'uwbSessionSuspended', handler);
    },

    addUwbTokenReceivedListener(handler) {
      return addListenerUnsub(advertiser, 'uwbTokenReceived', handler);
    },

    addBlePeerGpsUpdateListener(handler) {
      return addListenerUnsub(advertiser, 'blePeerGpsUpdate', handler);
    },

    getBluetoothState() {
      return advertiser.getBluetoothState();
    },

    drainNativeEvents() {
      return advertiser.drainNativeEvents();
    },

    addBluetoothStateListener(handler) {
      return addListenerUnsub(advertiser, 'stateChanged', (data: { state: string }) => {
        handler(data.state);
      });
    },
  };
}
